import * as Sentry from '@sentry/browser'
import livewireComponentId from '../global/livewireComponentId.js'
import { enqueueSerializedRequest } from '../global/serializedRequestQueue.js'

export default ({
  queueKey,
  audienceFlushEvent,
  debounce = 650,
  errorTitle = 'Error',
  errorMessage = 'Save failed'
}) => {
  let wireId = null

  return {
    saving: false,
    savedFlash: false,
    saveFailed: false,
    requestFailed: false,
    publishing: false,
    _saveTimer: null,
    _flashTimer: null,
    _saveGeneration: 0,
    _savePromise: null,
    _mutationPromise: null,
    _retryAutosave: false,
    _navigationPromise: null,
    _allowNavigation: false,
    _navigateHandler: null,
    _beforeUnloadHandler: null,

    init() {
      wireId = livewireComponentId(this.$root)
      this._navigateHandler = event => this._handleNavigate(event)
      this._beforeUnloadHandler = event => this._handleBeforeUnload(event)
      document.addEventListener('livewire:navigate', this._navigateHandler)
      window.addEventListener('beforeunload', this._beforeUnloadHandler)
    },

    _livewire() {
      return wireId && window.Livewire ? (window.Livewire.find(wireId) || null) : null
    },

    queueFromEvent(event) {
      if (!event?.target?.closest?.('[data-poll-autosave-fields]')) return
      if (event.target.closest('[data-poll-autosave-structural]')) return

      this.queueSave()
    },

    queueSave() {
      const generation = ++this._saveGeneration

      this.saving = true
      this.savedFlash = false
      this.saveFailed = false
      this.requestFailed = false
      this._retryAutosave = false
      clearTimeout(this._saveTimer)
      this._saveTimer = setTimeout(() => {
        this._saveTimer = null
        this._sendSave(generation)
      }, debounce)
    },

    mutate(method, ...args) {
      return this._mutate(method, true, args)
    },

    mutateWithoutAutosave(method, ...args) {
      return this._mutate(method, false, args)
    },

    _mutate(method, autosave, args) {
      const hadPendingSave = Boolean(this._saveTimer)

      this.saving = true
      this.savedFlash = false
      this.saveFailed = false
      this.requestFailed = false
      this._retryAutosave = false
      clearTimeout(this._saveTimer)
      this._saveTimer = null

      const request = enqueueSerializedRequest(queueKey, async() => {
        const wire = this._livewire()
        if (!wire) throw new Error('pollDraftAutosave: Livewire component not resolvable')

        return wire[method](...args)
      })
      const mutation = request
        .then(result => {
          this.requestFailed = false
          this._retryAutosave = false
          if (result === false) return this._markSaveFailed()

          if (autosave || hadPendingSave) {
            this.queueSave()
          } else {
            this.saving = false
          }

          return true
        })
        .catch(error => this._reportRequestError(error))

      this._mutationPromise = mutation
      mutation.finally(() => {
        if (this._mutationPromise === mutation) this._mutationPromise = null
      })

      return mutation
    },

    _sendSave(generation) {
      const queued = enqueueSerializedRequest(queueKey, async() => {
        const wire = this._livewire()
        if (!wire) throw new Error('pollDraftAutosave: Livewire component not resolvable')

        return wire.autosave()
      })
      const request = queued
        .then(result => {
          const saved = result !== false

          if (generation === this._saveGeneration) {
            this.requestFailed = false
            this._retryAutosave = false
            this.saveFailed = !saved
            this.savedFlash = saved
            clearTimeout(this._flashTimer)

            if (saved) {
              this._flashTimer = setTimeout(() => { this.savedFlash = false }, 2500)
            }
          }

          return saved
        })
        .catch(error => this._reportRequestError(error, true))

      this._savePromise = request
      request.finally(() => {
        if (generation === this._saveGeneration) this.saving = false
        if (this._savePromise === request) this._savePromise = null
      })

      return request
    },

    async flush() {
      if (this._mutationPromise) {
        const mutated = await this._mutationPromise
        if (mutated === false) return false
      }

      if (this._saveTimer) {
        clearTimeout(this._saveTimer)
        this._saveTimer = null
        return this._sendSave(this._saveGeneration)
      }

      if (this._savePromise) return this._savePromise

      if (this.requestFailed && this._retryAutosave) {
        this.saving = true

        return this._sendSave(this._saveGeneration)
      }

      return !this.saveFailed && !this.requestFailed
    },

    _collectAudienceSaves() {
      const audienceSaves = []
      window.dispatchEvent(new CustomEvent(audienceFlushEvent, {
        detail: {
          queueKey,
          waitUntil: promise => audienceSaves.push(Promise.resolve(promise))
        }
      }))

      return audienceSaves
    },

    _hasPendingAudienceWork() {
      let pending = false
      window.dispatchEvent(new CustomEvent(audienceFlushEvent, {
        detail: {
          queueKey,
          probeOnly: true,
          markPending: () => { pending = true }
        }
      }))

      return pending
    },

    _hasPendingWork() {
      return Boolean(
        this._saveTimer || this._savePromise || this._mutationPromise || this.requestFailed
      )
    },

    async flushAll(audienceSaves = this._collectAudienceSaves()) {
      const audienceResults = await Promise.all(audienceSaves)
      if (audienceResults.includes(false)) return this._markSaveFailed()
      if (audienceResults.length > 0) this.saveFailed = false

      const saved = await this.flush()
      if (saved === false) return this._markSaveFailed()

      return true
    },

    async publish(method) {
      if (this.publishing) return false

      this.publishing = true
      this.saving = true
      this.saveFailed = false
      this.savedFlash = false

      try {
        if (await this.flushAll() === false) return false

        const result = await enqueueSerializedRequest(queueKey, async() => {
          const wire = this._livewire()
          if (!wire) throw new Error('pollDraftAutosave: Livewire component not resolvable')

          return wire[method]()
        })

        this.requestFailed = false
        this._retryAutosave = false

        return result !== false
      } catch (error) {
        return this._reportRequestError(error)
      } finally {
        this.publishing = false
        this.saving = false
      }
    },

    _handleNavigate(event) {
      if (this._allowNavigation) {
        this._allowNavigation = false

        return
      }

      if (this.publishing || !event?.cancelable || !event?.detail?.url) return

      // Alpine's public navigate API always creates a new history entry. A
      // cancelled back/forward navigation cannot therefore be replayed with
      // equivalent semantics, so leave browser-history navigation untouched.
      if (event.detail.history) return

      const audienceSaves = this._collectAudienceSaves()
      const hasPendingSave = Boolean(
        this._hasPendingWork() || audienceSaves.length
      )
      if (!hasPendingSave) return

      event.preventDefault()
      if (this._navigationPromise) return

      const navigation = this.flushAll(audienceSaves)
        .then(saved => {
          if (!saved) return false

          const livewire = window.Livewire
          if (!livewire?.navigate) {
            throw new Error('pollDraftAutosave: Livewire navigation not resolvable')
          }

          this._allowNavigation = true
          livewire.navigate(event.detail.url)

          return true
        })
        .catch(error => this._reportRequestError(error))

      this._navigationPromise = navigation
      navigation.finally(() => {
        if (this._navigationPromise === navigation) this._navigationPromise = null
      })
    },

    _handleBeforeUnload(event) {
      if (!this._hasPendingWork() && !this._hasPendingAudienceWork()) return

      event.preventDefault()
      event.returnValue = ''
    },

    _markSaveFailed() {
      this.saveFailed = true
      this.savedFlash = false

      return false
    },

    _reportRequestError(error, retryAutosave = false) {
      this.requestFailed = true
      this._retryAutosave = retryAutosave
      this._markSaveFailed()
      window.dispatchEvent(new CustomEvent('notify', {
        detail: { type: 'danger', title: errorTitle, message: errorMessage }
      }))
      Sentry.captureException(error)
      console.error(error)

      return false
    },

    destroy() {
      if (this._saveTimer) this.flush()
      clearTimeout(this._flashTimer)
      if (this._navigateHandler) {
        document.removeEventListener('livewire:navigate', this._navigateHandler)
        this._navigateHandler = null
      }
      if (this._beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
        this._beforeUnloadHandler = null
      }
    }
  }
}
